import {
  Box,
  Flex,
  Grid,
  GridItem,
  Icon,
  Spacer,
  Text,
} from '@chakra-ui/react';
import _ from 'lodash';
import React from 'react';

import { date as commonDate } from '@finley-technologies/common';
import { CreditAgreement, Util } from '@finley-technologies/finley-types';

import {
  AssignmentSummaryAccordion,
  AssignmentSummaryAccordionRow,
  DollarWithCircleIcon,
} from './SharedComponents';
import type { PortfolioStat } from './SharedComponents';
import { borrowingBaseSummaryFormatterMap } from 'packages/borrowing-base/ui/borrowingBaseSummary/utils';
import { colors } from 'packages/common-frontend/colors';
import { Icon as FAIcon } from 'packages/common-frontend/components/icons';
import { CalculationTerminologiesPopover } from 'packages/resources/ui/CalculationTerminologiesPopover';
import { FiPercent } from 'react-icons/fi';

const StatHeader: React.FC<{
  initial: React.ReactNode;
  proposed: React.ReactNode;
}> = ({ initial, proposed }) => (
  <Grid templateColumns="repeat(4, 1fr)" templateRows="repeat(1, 80px)">
    <GridItem colSpan={2} />
    <GridItem
      colSpan={1}
      borderBottom="1px solid"
      borderColor={colors.gray[400]}
      alignSelf="end"
      pb="8px"
      pl="16px"
    >
      {initial}
    </GridItem>
    <GridItem
      colSpan={1}
      borderBottom="1px solid"
      borderColor={colors.gray[400]}
      alignSelf="end"
    >
      <Box
        bg={colors.aurora[900]}
        color="white"
        p="16px 16px"
        borderRadius="8px 8px 0 0"
      >
        {proposed}
      </Box>
    </GridItem>
  </Grid>
);

const StatRow: React.FC<{
  initial: React.ReactNode;
  proposed: React.ReactNode;
  label?: React.ReactNode;
}> = ({ label, initial, proposed }) => {
  return (
    <Grid templateColumns="repeat(4, 1fr)" templateRows="repeat(1, 56px)">
      <GridItem
        colSpan={2}
        color={colors.aurora[900]}
        borderBottom="2px solid"
        borderColor={colors.aurora[900]}
        alignSelf="end"
        p="16px"
        height="64px"
        display="flex"
        alignItems="center"
      >
        {label}
      </GridItem>
      <GridItem
        colSpan={1}
        borderBottom="2px solid"
        borderColor={colors.aurora[900]}
        alignSelf="end"
        p="16px"
        height="64px"
        display="flex"
        alignItems="center"
      >
        {initial}
      </GridItem>
      <GridItem
        colSpan={1}
        alignSelf="end"
        bg={colors.aurora[900]}
        height="64px"
      >
        <Flex height="100%" color="white" p="16px" alignItems="center">
          {proposed}
        </Flex>
      </GridItem>
    </Grid>
  );
};

export const AssignmentSummaryTopLevelStats: React.FC<{
  reportingDate: Util.FullDateStr;
  initial: PortfolioStat;
  proposed: PortfolioStat;
  creditAgreement: CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
}> = ({ initial, proposed, reportingDate, creditAgreement }) => {
  const {
    borrowingBaseMechanics: {
      calcAgentMechanics: { loanProductMechanics },
    },
  } = creditAgreement;
  return (
    <>
      <StatHeader
        initial={
          <>
            <Text fontWeight="bold" pb="24px">
              Current Portfolio
            </Text>
          </>
        }
        proposed={
          <>
            <Text fontWeight="bold">Proposed Portfolio</Text>
            <Text fontSize="smaller">
              {`Reporting Date: ${commonDate.fullDateStrToStr(
                reportingDate,
                'MMMM dd, yyyy',
              )}`}
            </Text>
          </>
        }
      />
      <AssignmentSummaryAccordion
        accordionButton={({ hasAccordionPanel, isExpanded }) => (
          <Box height="100%" width="100%" pt="8px">
            <StatRow
              label={
                <Flex alignItems="center">
                  <DollarWithCircleIcon mr="16px" />
                  <Text fontWeight="semibold" fontSize="larger">
                    Total Exposure
                  </Text>
                  <Box display="inline-block" ml="8px">
                    <CalculationTerminologiesPopover termKeyLabel="Total Receivable Exposure" />
                  </Box>
                </Flex>
              }
              initial={
                <Text fontWeight="medium" textAlign="justify">
                  {borrowingBaseSummaryFormatterMap.currency(
                    initial.totalExposure,
                  )}
                </Text>
              }
              proposed={
                <>
                  <Text fontWeight="medium">
                    {borrowingBaseSummaryFormatterMap.currency(
                      proposed.totalExposure,
                    )}
                  </Text>
                  <Spacer />
                  {hasAccordionPanel && (
                    <FAIcon
                      icon={
                        isExpanded ? 'circle-chevron-up' : 'circle-chevron-down'
                      }
                      size="large"
                      color={colors.white}
                    />
                  )}
                </>
              }
            />
          </Box>
        )}
        accordionPanel={
          <Box mt="24px">
            {_.map(loanProductMechanics, (loanProduct) => {
              return (
                <AssignmentSummaryAccordionRow
                  key={`loan-product-exposure-${loanProduct.entity.id}`}
                  subLabel={loanProduct.entity.name}
                  initialValue={
                    initial.totalExposureByLoanProductId[loanProduct.entity.id]
                  }
                  proposedValue={
                    proposed.totalExposureByLoanProductId[loanProduct.entity.id]
                  }
                  valueType="currency"
                  background="light"
                />
              );
            })}
          </Box>
        }
        isTopLevel
      />
      <Box mt="24px">
        <StatRow
          label={
            <Flex alignItems="center">
              <DollarWithCircleIcon mr="16px" />
              <Text fontSize="larger" fontWeight="semibold">
                Total Borrowing Base from Receivables
              </Text>
              <Box display="inline-block" ml="8px">
                <CalculationTerminologiesPopover termKeyLabel="Total Receivable Borrowing Base Amount" />
              </Box>
            </Flex>
          }
          initial={
            <Text fontWeight="medium">
              {borrowingBaseSummaryFormatterMap.currency(
                initial.totalBorrowingBaseAmount,
              )}
            </Text>
          }
          proposed={
            <Text fontWeight="medium">
              {borrowingBaseSummaryFormatterMap.currency(
                proposed.totalBorrowingBaseAmount,
              )}
            </Text>
          }
        />
      </Box>
      <Box mt="24px">
        <StatRow
          label={
            <Flex alignItems="center">
              <Icon fontSize="22px" alignSelf="center" mr="16px">
                <FiPercent />
              </Icon>
              <Text fontSize="larger" fontWeight="semibold">
                Receivable Effective Advance Rate
              </Text>
              <Box display="inline-block" ml="8px">
                <CalculationTerminologiesPopover termKeyLabel="Effective Receivable Advance Rate" />
              </Box>
            </Flex>
          }
          initial={
            <Text fontWeight="medium">
              {borrowingBaseSummaryFormatterMap.percentage(
                initial.effectiveReceivableAdvanceRate,
              )}
            </Text>
          }
          proposed={
            <Text fontWeight="medium">
              {borrowingBaseSummaryFormatterMap.percentage(
                proposed.effectiveReceivableAdvanceRate,
              )}
            </Text>
          }
        />
      </Box>
    </>
  );
};
